export interface UploadProgress {
  loaded: number;
  total: number;
}

export interface PutWithProgressOptions {
  onProgress?: (p: UploadProgress) => void;
  /** Sin bytes nuevos durante este tiempo ⇒ se aborta. Por defecto 30 s. */
  stallMs?: number;
  signal?: AbortSignal;
}

export class UploadHttpError extends Error {
  public readonly httpStatus?: number;

  constructor(message: string, httpStatus?: number) {
    super(message);
    this.name = 'UploadHttpError';
    this.httpStatus = httpStatus;
  }
}

export class UploadStalledError extends Error {
  public readonly code = 'STALLED' as const;

  constructor(stallMs: number) {
    super(`Subida estancada: ${stallMs} ms sin bytes nuevos`);
    this.name = 'UploadStalledError';
  }
}

export const DEFAULT_STALL_MS = 30_000;

/**
 * PUT con XMLHttpRequest en lugar de `fetch`.
 *
 * ─── Por qué XHR y no fetch ────────────────────────────────────────────────
 *
 * `fetch` NO expone eventos de progreso de SUBIDA. Es una limitación del
 * estándar: solo XHR tiene `xhr.upload.onprogress`. Sin bytes no se puede
 * pintar una barra y, lo importante, no se puede distinguir «va lento» de «se
 * ha muerto» — que es exactamente el fallo que arreglamos: hoy una subida con
 * cobertura mala se queda colgada hasta que el sistema tira el socket, sin
 * ninguna señal, y el operario cree que la app se ha roto.
 *
 * ─── Por qué NO hay timeout total ──────────────────────────────────────────
 *
 * A propósito. Un tope fijo mataría subidas lentas pero perfectamente sanas,
 * que es justo lo que no queremos. El freno es por ESTANCAMIENTO: se aborta
 * solo si no entran bytes nuevos durante `stallMs`. Mientras la barra avanza,
 * por despacio que sea, se deja terminar.
 *
 * ─── Dos trampas de CORS ───────────────────────────────────────────────────
 *
 * 1. Los oyentes de `xhr.upload` DEBEN registrarse antes de `send()`. Si se
 *    registran después, en peticiones cross-origin (nuestro caso: Hetzner) los
 *    eventos no se disparan nunca.
 * 2. Escuchar `xhr.upload` convierte la petición en «no simple» y fuerza un
 *    preflight OPTIONS. Aquí no cambia nada: ya mandábamos
 *    `Content-Type: image/jpeg`, que tampoco es un valor simple, así que el
 *    preflight ya ocurría y el bucket ya lo permite.
 */
export function putWithProgress(
  url: string,
  blob: Blob,
  contentType: string,
  { onProgress, stallMs = DEFAULT_STALL_MS, signal }: PutWithProgressOptions,
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    let stallTimer: ReturnType<typeof setTimeout> | undefined;
    let settled = false;

    /**
     * Motivo real de un abort provocado por nosotros.
     *
     * `xhr.abort()` dispara `onabort` de forma SÍNCRONA, así que si primero se
     * aborta y después se rechaza, gana el genérico «Subida abortada» y se
     * pierde la causa (estancamiento vs cancelación). El pool clasifica por el
     * tipo de error, así que perderla significaba reintentar mal.
     */
    let abortReason: Error | undefined;

    const abortWith = (reason: Error) => {
      abortReason = reason;
      xhr.abort();
      fail(reason);
    };

    const onExternalAbort = () => {
      abortWith(new UploadHttpError('Subida cancelada'));
    };

    const cleanup = () => {
      if (stallTimer) clearTimeout(stallTimer);
      signal?.removeEventListener('abort', onExternalAbort);
    };

    function fail(err: Error) {
      if (settled) return;
      settled = true;
      cleanup();
      reject(err);
    }

    const done = () => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve();
    };

    const armStallTimer = () => {
      if (stallTimer) clearTimeout(stallTimer);
      stallTimer = setTimeout(() => {
        abortWith(new UploadStalledError(stallMs));
      }, stallMs);
    };

    // ANTES de send(): ver trampa 1 en el bloque de arriba.
    xhr.upload.addEventListener('progress', (e: ProgressEvent) => {
      armStallTimer();
      if (e.lengthComputable) {
        onProgress?.({ loaded: e.loaded, total: e.total });
      }
    });

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        done();
        return;
      }
      fail(
        new UploadHttpError(
          `PUT falló: ${xhr.status} ${xhr.statusText}`,
          xhr.status,
        ),
      );
    };
    xhr.onerror = () => fail(new UploadHttpError('Error de red en el PUT'));
    xhr.onabort = () =>
      fail(abortReason ?? new UploadHttpError('Subida abortada'));

    signal?.addEventListener('abort', onExternalAbort);

    xhr.open('PUT', url, true);
    xhr.setRequestHeader('Content-Type', contentType);
    armStallTimer();
    xhr.send(blob);
  });
}
